import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import { unified } from 'unified'
import type { Heading, Root } from 'mdast'
import { AlertTriangle, BarChart3, Check, ChevronDown, Clipboard, Clock3, Download, FileText, Link2, LoaderCircle, RefreshCw, RotateCw, ServerCrash, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import './api-docs.css'

const DOCS_ENDPOINT = '/api/v1/docs/api'
const POLL_INTERVAL_MS = 45_000

type DocHeading = { id: string; depth: number; text: string }
type HeadingData = NonNullable<Heading['data']> & { hProperties?: Record<string, unknown> }
type RemarkNode = { type: string; value?: string; alt?: string | null; depth?: number; children?: RemarkNode[]; data?: HeadingData }
type MarkdownTextNode = { type: string; value?: string; alt?: string | null; children?: MarkdownTextNode[] }

function headingText(node: MarkdownTextNode): string {
  if (node.type === 'image' || node.type === 'imageReference') return node.alt ?? ''
  if (node.value) return node.value
  return node.children?.map((child) => headingText(child)).join('') ?? ''
}

function slugBase(text: string) {
  return text.normalize('NFKC').toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}\s_-]/gu, '')
    .trim()
    .replace(/[\s_]+/g, '-') || 'section'
}

function visitRemarkNodes(node: RemarkNode, visit: (node: RemarkNode) => void) {
  visit(node)
  node.children?.forEach((child) => visitRemarkNodes(child, visit))
}

function collectHeadings(tree: Root): DocHeading[] {
  const counts = new Map<string, number>()
  const result: DocHeading[] = []
  visitRemarkNodes(tree as unknown as RemarkNode, (node) => {
    if (node.type !== 'heading') return
    const text = node.children?.map((child) => headingText(child)).join('').trim() ?? ''
    if (!text) return
    const base = slugBase(text)
    const count = counts.get(base) ?? 0
    counts.set(base, count + 1)
    result.push({ id: count ? `${base}-${count}` : base, depth: node.depth ?? 1, text })
  })
  return result
}

function remarkHeadingIds() {
  return (tree: Root) => {
    const headings = collectHeadings(tree)
    let headingIndex = 0
    visitRemarkNodes(tree as unknown as RemarkNode, (node) => {
      if (node.type !== 'heading') return
      const text = node.children?.map((child) => headingText(child)).join('').trim() ?? ''
      if (!text) return
      const item = headings[headingIndex]
      headingIndex += 1
      if (!item) return
      const data = (node.data ?? {}) as HeadingData
      node.data = { ...data, hProperties: { ...data.hProperties, id: item.id } }
    })
  }
}

function remarkMarkApiPaths() {
  return (tree: Root) => {
    const visit = (node: RemarkNode) => {
      if (node.type === 'inlineCode' && node.value && isApiPath(node.value)) {
        const data = node.data ?? {}
        const classNames = data.hProperties?.className
        const existingClassNames = Array.isArray(classNames) ? classNames : typeof classNames === 'string' ? [classNames] : []
        node.data = { ...data, hProperties: { ...data.hProperties, className: [...existingClassNames, 'api-docs-inline-api-path'] } }
      }
      node.children?.forEach(visit)
    }
    visit(tree)
  }
}

function getReactText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getReactText).join('')
  if (node && typeof node === 'object' && 'props' in node) {
    return getReactText((node as { props: { children?: ReactNode } }).props.children)
  }
  return ''
}

function isApiPath(value: string) {
  return /^(?:(?:GET|POST|PUT|PATCH|DELETE)\s+)?\/(?:api\/v1|stocks)(?:\/|$)/i.test(value.trim())
}

function CopyButton({ value, label, compact = false }: { value: string; label: string; compact?: boolean }) {
  const [status, setStatus] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timerRef = useRef<number | null>(null)
  useEffect(() => () => { if (timerRef.current !== null) window.clearTimeout(timerRef.current) }, [])

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard-unavailable')
      await navigator.clipboard.writeText(value)
      setStatus('copied')
    } catch {
      setStatus('failed')
    }
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => setStatus('idle'), 2200)
  }

  const accessibleLabel = status === 'copied' ? '已复制' : status === 'failed' ? '复制失败' : label
  return (
    <span className={`api-docs-copy-wrap ${compact ? 'compact' : ''}`}>
      <button type="button" className="api-docs-copy" onClick={() => void copy()} aria-label={accessibleLabel} title={status === 'failed' ? '复制失败，请检查浏览器剪贴板权限' : accessibleLabel}>
        {status === 'copied' ? <Check size={14} aria-hidden="true" /> : status === 'failed' ? <AlertTriangle size={14} aria-hidden="true" /> : <Clipboard size={14} aria-hidden="true" />}
        {!compact && <span>{status === 'copied' ? '已复制' : status === 'failed' ? '复制失败' : '复制'}</span>}
      </button>
      <span className="api-docs-sr-only" aria-live="polite">{status === 'copied' ? '已复制到剪贴板' : status === 'failed' ? '复制失败，请检查浏览器剪贴板权限' : ''}</span>
    </span>
  )
}

function LoadingState() {
  return <div className="api-docs-state" role="status"><LoaderCircle className="api-docs-spin" size={22} /><strong>正在读取接口文档</strong><span>正在从服务端获取最新 Markdown…</span></div>
}

function ErrorState({ error, retry }: { error: string; retry: () => void }) {
  return <section className="api-docs-state error" role="alert"><ServerCrash size={23} /><strong>文档暂时不可用</strong><span>{error}</span><button type="button" className="api-docs-button primary" onClick={retry}><RotateCw size={15} />重试</button></section>
}

function formatCheckTime(value: Date | null) {
  if (!value) return '尚未同步'
  return new Intl.DateTimeFormat('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(value)
}

export function ApiDocsPage() {
  const [tocExpanded, setTocExpanded] = useState(false)
  const [markdown, setMarkdown] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [checking, setChecking] = useState(true)
  const [lastChecked, setLastChecked] = useState<Date | null>(null)
  const etagRef = useRef<string | null>(null)
  const requestRef = useRef<AbortController | null>(null)
  const resumeCheckAtRef = useRef(0)
  const downloadTimersRef = useRef(new Map<string, number>())

  useEffect(() => {
    const downloads = downloadTimersRef.current
    return () => {
      downloads.forEach((timer, url) => {
        window.clearTimeout(timer)
        URL.revokeObjectURL(url)
      })
      downloads.clear()
    }
  }, [])

  const downloadMarkdown = () => {
    if (markdown === null) return
    const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = '股票数据服务-API文档.md'
    document.body.appendChild(link)
    try {
      link.click()
    } finally {
      link.remove()
      // Give the browser time to start reading the file before releasing its URL.
      const timer = window.setTimeout(() => {
        URL.revokeObjectURL(url)
        downloadTimersRef.current.delete(url)
      }, 1000)
      downloadTimersRef.current.set(url, timer)
    }
  }

  const refresh = useCallback(async () => {
    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    setChecking(true)
    setError(null)
    try {
      const headers = new Headers({ Accept: 'text/markdown, text/plain;q=0.9, */*;q=0.1' })
      if (etagRef.current) headers.set('If-None-Match', etagRef.current)
      const response = await fetch(DOCS_ENDPOINT, { method: 'GET', headers, cache: 'no-cache', signal: controller.signal })
      if (response.status === 304) {
        if (requestRef.current === controller && !controller.signal.aborted) setLastChecked(new Date())
        return
      }
      if (!response.ok) throw new Error(`文档请求失败（HTTP ${response.status}）`)
      const nextMarkdown = await response.text()
      if (!nextMarkdown.trim()) throw new Error('服务端返回的 Markdown 文档为空。')
      if (requestRef.current !== controller || controller.signal.aborted) return
      etagRef.current = response.headers.get('ETag')
      setMarkdown(nextMarkdown)
      setLastChecked(new Date())
    } catch (requestError) {
      if (controller.signal.aborted || requestRef.current !== controller) return
      setError(requestError instanceof Error ? requestError.message : '读取文档失败，请稍后重试。')
    } finally {
      if (requestRef.current === controller) setChecking(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, POLL_INTERVAL_MS)
    const onResume = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - resumeCheckAtRef.current < 900) return
      resumeCheckAtRef.current = now
      void refresh()
    }
    document.addEventListener('visibilitychange', onResume)
    window.addEventListener('focus', onResume)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onResume)
      window.removeEventListener('focus', onResume)
      requestRef.current?.abort()
      requestRef.current = null
    }
  }, [refresh])

  const headings = useMemo(() => {
    if (!markdown) return []
    return collectHeadings(unified().use(remarkParse).use(remarkGfm).parse(markdown) as Root)
  }, [markdown])
  const initialLoading = markdown === null && checking && error === null
  const initialError = markdown === null && error !== null

  return (
    <div className="api-docs-shell">
      <header className="api-docs-topbar">
        <Link className="api-docs-brand" to="/stocks" aria-label="返回股票查询">
          <span className="api-docs-brand-icon"><BarChart3 size={19} aria-hidden="true" /></span>
          <span><strong>个股查询</strong><small>API 文档</small></span>
        </Link>
        <nav className="api-docs-topnav" aria-label="主导航">
          <Link to="/stocks" aria-label="股票查询"><BarChart3 size={16} aria-hidden="true" /><span className="nav-link-label">股票查询</span></Link>
          <Link to="/api-docs" aria-label="API 文档" aria-current="page"><FileText size={16} aria-hidden="true" /><span className="nav-link-label">API 文档</span></Link>
          <Link to="/admin" aria-label="管理后台"><ShieldCheck size={16} aria-hidden="true" /><span className="nav-link-label">管理后台</span></Link>
        </nav>
      </header>

      <main className="api-docs-layout">
        <aside className={`api-docs-sidebar${tocExpanded ? ' expanded' : ''}`} aria-label="文档章节目录">
          <div className="api-docs-side-title"><h2>文档目录</h2><span>V1</span><button type="button" className="api-docs-toc-toggle" onClick={() => setTocExpanded((expanded) => !expanded)} aria-expanded={tocExpanded} aria-controls="api-docs-toc"><span>{tocExpanded ? '收起目录' : '展开目录'}</span><ChevronDown size={16} aria-hidden="true" /></button></div>
          {headings.length > 0 ? <nav id="api-docs-toc" aria-label="Markdown 标题目录"><ol>{headings.map((item) => <li className={`depth-${item.depth}`} key={`${item.id}-${item.depth}`}><a href={`#${item.id}`}>{item.text}</a></li>)}</ol></nav> : <p className="api-docs-toc-empty">{markdown ? '文档没有标题' : '读取章节目录…'}</p>}
          <div className="api-docs-side-note"><Clock3 size={14} aria-hidden="true" /><span>上次检查<br /><strong>{formatCheckTime(lastChecked)}</strong></span></div>
        </aside>

        <section className="api-docs-main" aria-labelledby="api-docs-title" aria-busy={initialLoading || checking}>
          <div className="api-docs-heading">
            <div><h1 id="api-docs-title">API 接口文档</h1><p className="api-docs-subtitle">股票数据接口说明与请求示例。文档随服务端自动更新。</p></div>
            <div className="api-docs-actions">
              <button type="button" className="api-docs-button" onClick={() => void refresh()} disabled={checking} aria-label="立即检查文档更新" title="立即检查文档更新">
                <RefreshCw className={checking ? 'api-docs-spin' : ''} size={16} aria-hidden="true" /><span>{checking ? '检查中' : '检查更新'}</span>
              </button>
              <button type="button" className="api-docs-button primary" onClick={downloadMarkdown} disabled={markdown === null} title={markdown !== null ? '下载当前完整文档（Markdown）' : '文档加载完成后可下载'}>
                <Download size={16} aria-hidden="true" /><span>下载 Markdown</span>
              </button>
            </div>
          </div>

          {initialLoading && <LoadingState />}
          {initialError && <ErrorState error={error} retry={() => void refresh()} />}
          {markdown !== null && <>
            {error && <div className="api-docs-sync-warning" role="status"><AlertTriangle size={16} aria-hidden="true" /><span>同步失败，当前保留已加载的文档。{error}</span><button type="button" onClick={() => void refresh()} disabled={checking}><RotateCw size={14} aria-hidden="true" />重试</button></div>}
            <article className="api-docs-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkHeadingIds, remarkMarkApiPaths]}
                components={{
                  a: ({ node: _node, href, children, ...props }) => {
                    const external = Boolean(href && /^https?:\/\//i.test(href))
                    return <a href={href} {...props} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{children}</a>
                  },
                  code: ({ node: _node, children, className, ...props }) => {
                    const value = getReactText(children).replace(/\n$/, '')
                    const apiPath = String(className ?? '').includes('api-docs-inline-api-path')
                    return <><code className={className} {...props}>{children}</code>{apiPath && isApiPath(value) && <CopyButton value={value} label="复制接口路径" compact />}</>
                  },
                  pre: ({ children }) => <div className="api-docs-code-block"><div className="api-docs-code-toolbar"><span>示例代码</span><CopyButton value={getReactText(children).replace(/\n$/, '')} label="复制代码块" /></div><pre>{children}</pre></div>,
                  h1: ({ node: _node, children, id, ...props }) => <div className="api-docs-heading-wrap"><h1 id={id} {...props}>{children}</h1><a className="api-docs-heading-anchor" href={`#${id}`} aria-label={`定位到${getReactText(children)}`}><Link2 size={15} aria-hidden="true" /></a></div>,
                  h2: ({ node: _node, children, id, ...props }) => <div className="api-docs-heading-wrap"><h2 id={id} {...props}>{children}</h2><a className="api-docs-heading-anchor" href={`#${id}`} aria-label={`定位到${getReactText(children)}`}><Link2 size={14} aria-hidden="true" /></a></div>,
                  h3: ({ node: _node, children, id, ...props }) => <div className="api-docs-heading-wrap"><h3 id={id} {...props}>{children}</h3><a className="api-docs-heading-anchor" href={`#${id}`} aria-label={`定位到${getReactText(children)}`}><Link2 size={13} aria-hidden="true" /></a></div>,
                }}
              >{markdown}</ReactMarkdown>
            </article>
            <div className="api-docs-refresh-meta" aria-live="polite">{checking ? <><LoaderCircle className="api-docs-spin" size={14} />正在检查服务端更新</> : <>最近检查于 {formatCheckTime(lastChecked)} · 每 45 秒自动检查</>}</div>
          </>}
        </section>
      </main>
    </div>
  )
}
